import { useMemo } from "react";
import { capitalize } from "lodash-es";
import { ReactComponent as DownIcon } from "../../../img/Down.svg";
import { Filter, FiltersInterface } from "../../../config/consts";
import styled from "@emotion/styled";

interface FilterButtonContentProps<T extends FiltersInterface[keyof FiltersInterface]> {
  confirmedOptions: T | undefined;
  filterType: Filter;
  localOptions: T | undefined;
}

export const FilterButtonContent = <T extends FiltersInterface[keyof FiltersInterface]>({
  confirmedOptions,
  filterType,
  localOptions,
}: FilterButtonContentProps<T>) => {
  const optionConfirmed = confirmedOptions !== null && Array.isArray(confirmedOptions);
  const isMultipleOptionsConfirmed = optionConfirmed && confirmedOptions.length > 1;
  const confirmedOptionCount = isMultipleOptionsConfirmed ? confirmedOptions.length : 1;

  const buttonText = useMemo(() => {
    if (filterType === Filter.Amount) {
      return formatAmountButtonText(
        confirmedOptions as FiltersInterface[Filter.Amount],
        filterType
      );
    }
    const isOneOptionConfirmed = optionConfirmed && confirmedOptions.length === 1;
    const firstOptionName = isOneOptionConfirmed && confirmedOptions[0];
    return isOneOptionConfirmed ? firstOptionName : capitalize(filterType);
  }, [confirmedOptions, filterType, optionConfirmed]);

  /* If only one option has been confirmed,
     the text inside the button will display the confirmed option; 
     otherwise, it will show a count of the options confirmed */

  return (
    <>
      {buttonText}{" "}
      <FilterOptionCount
        isVisible={isMultipleOptionsConfirmed}
        confirmedOptionCount={confirmedOptionCount}
      />
      <DownIcon width={20} height={20} stroke={localOptions != null ? "black" : "white"} />
    </>
  );
};

// Sub components

const FilterOptionCount = ({
  isVisible,
  confirmedOptionCount,
}: {
  isVisible: boolean;
  confirmedOptionCount: number;
}) => {
  if (isVisible) {
    const onlyOneDigit = confirmedOptionCount < 10;
    return (
      <FilterOptionCountContainer isCircle={onlyOneDigit}>
        {confirmedOptionCount}
      </FilterOptionCountContainer>
    );
  }
  return null;
};

const FilterOptionCountContainer = styled.div<{ isCircle: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${props => (props.isCircle ? "50%" : "200px")};
  padding: ${props => (props.isCircle ? "0" : "3px 7.5px")};
  height: 20px;
  width: ${props => (props.isCircle ? "20px" : "fit-content")};
  background: ${props => props.theme.surface.main.primary.default};
  color: ${props => props.theme.font.primary.default};
  font-weight: ${props => props.theme.fontWeights["work-sans-0"]};
  font-size: ${props => props.theme.fontSize["4"]};
`;

// Helpers

function formatAmountButtonText(
  confirmedOptions: FiltersInterface[Filter.Amount],
  filterType: Filter
) {
  const defaultLabel = capitalize(filterType);
  if (confirmedOptions == null || (confirmedOptions.min == null && confirmedOptions.max == null)) {
    return defaultLabel;
  }

  const { min, max } = confirmedOptions;
  if (min != null && max != null && min.value.lessThanOrEqualTo(max.value)) {
    if (min.value.eq(max.value)) {
      return `Exactly $${min.formatted}`;
    }
    return `Between $${min.formatted} and $${max.formatted}`;
  }
  if (min == null && max != null) {
    return `Up to $${max.formatted}`;
  }
  if (min != null && max == null) {
    return `Above $${min.formatted}`;
  }

  return defaultLabel;
}
